import React, { useState, useCallback } from 'react';
import dateFormat from 'dateformat';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import CloseIcon from '@mui/icons-material/Close';
import DialogActions from '@mui/material/DialogActions';
import FormGroup from '@mui/material/FormGroup';
import Checkbox from '@mui/material/Checkbox';
import { downloadFile } from '#action/download';
import {
  ReportDialogPaper,
  ReportDialogIcon,
  ReportDialogTitle,
  ReportDialogContent,
  ReportRadioGroup
} from './styles';
import buildReportPath, { outputFormatOptions } from './build-report-path';

const defaultContentOptions = [
  {
    name: 'locations',
    label: 'Map / Locations',
    checked: false
  },
  {
    name: 'temperatureChart',
    label: 'Temperature chart',
    checked: true,
    disabled: true
  },
  {
    name: 'humidityChart',
    label: 'Humidity chart',
    checked: false
  },
  {
    name: 'statisticalSummary',
    label: 'Statistical summary',
    checked: true
  },
  {
    name: 'measurementsTable',
    label: 'Measurements table',
    checked: false
  }
];

const ReportDialog = props => {
  const {
    showDialog,
    setReportDialog,
    dispatch,
    account,
    device,
    dateRange,
    deviceId
  } = props;

  const [measurementSystemValue, setMeasurementSystem] = useState(account.measurementSystem);
  const [outputFormat, setOutputFormat] = useState(outputFormatOptions.PDF);
  const [contentOptions, setContentOptions] = useState(defaultContentOptions);

  const closeDialog = () => setReportDialog(false);

  const handleExportClick = async () => {
    closeDialog();
    const path = buildReportPath({
      outputFormat,
      measurementSystem: measurementSystemValue,
      deviceId,
      startDate: new Date(dateRange[0]).toISOString(),
      endDate: new Date(dateRange[1]).toISOString(),
      contentOptions
    });

    const fileName = `${device.title}_${dateFormat(dateRange[0], 'dd-mm-yyyy')}_${dateFormat(dateRange[1], 'dd-mm-yyyy')}${outputFormat === outputFormatOptions.CSV ? '.csv' : '.pdf'}`;

    await dispatch(downloadFile(
      path,
      fileName
    ));
  };

  const handleContentOptionsChange = useCallback(e => {
    const updatedContentOptions = contentOptions.map(option =>
      (option.name === e.target.name ? { ...option, checked: e.target.checked } : option));
    setContentOptions(updatedContentOptions);
  }, [contentOptions]);

  return (
    <Dialog
      open={showDialog}
      onClose={closeDialog}
      PaperProps={{ style: ReportDialogPaper }}
      aria-labelledby="report-dialog"
      aria-describedby="report-dialog"
    >
      <ReportDialogTitle>
        Report options
        <ReportDialogIcon aria-label="close" onClick={closeDialog}>
          <CloseIcon />
        </ReportDialogIcon>
      </ReportDialogTitle>
      <ReportDialogContent>
        <Typography variant="h6">Units</Typography>
        <ReportRadioGroup
          row
          aria-label="Measurement system"
          name="measurementSystem"
          value={measurementSystemValue}
          onChange={e => setMeasurementSystem(e.target.value)}
        >
          <FormControlLabel value="metric" control={<Radio />} label="Metric (km, °C)" />
          <FormControlLabel value="us" control={<Radio />} label="Imperial (mi, °F)" />
        </ReportRadioGroup>

        <Typography variant="h6">Output format</Typography>
        <ReportRadioGroup
          row
          aria-label="outputFormat"
          name="outputFormat"
          value={outputFormat}
          onChange={e => setOutputFormat(e.target.value)}
        >
          {Object.entries(outputFormatOptions).map(([key, value]) =>
            <FormControlLabel
              key={key}
              value={value}
              control={<Radio />}
              label={value.toUpperCase()}
            />)}
        </ReportRadioGroup>
        {outputFormat === outputFormatOptions.PDF &&
          <>
            <Typography variant="h6">Content</Typography>
            <FormGroup>
              {contentOptions
                .map(({ name, label: formControlLabel, checked, disabled }) => (
                  <FormControlLabel
                    key={name}
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={handleContentOptionsChange}
                        name={name}
                        disabled={disabled}
                      />
                    }
                    label={formControlLabel}
                  />
                ))}
            </FormGroup>
          </>}
      </ReportDialogContent>
      <DialogActions>
        <Button onClick={closeDialog} variant="outlined" color="primary">
          Cancel
        </Button>
        <Button onClick={handleExportClick} variant="contained" color="secondary" autoFocus>
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportDialog;
